import React from 'react';
import { Check, Star, Award, Sparkles } from 'lucide-react';
import { clinicInfo } from '../mockData';
import { openWhatsApp } from '@/lib/whatsapp';

const SpecialtiesSection = () => {
  const handleWhatsAppClick = (treatment) => {
    openWhatsApp({
      section: `especialidades_${treatment.replace(/\s+/g, '_')}`,
      message: `Olá! Vim pelo site e quero saber mais sobre ${treatment}.`,
    });
  };

  return (
    <section className="overflow-hidden bg-brand-cream py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-ink/10 bg-white/80 px-4 py-2 shadow-sm backdrop-blur-sm">
            <Award className="h-4 w-4 text-brand-gold" strokeWidth={1.75} />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-deep">
              Especialidades
            </span>
          </div>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-brand-ink md:text-5xl">
            Líder em <span className="text-brand-deep">aparelhos</span> e{' '}
            <span className="text-brand-deep">implantes</span>
          </h2>
          <p className="mt-5 text-lg font-light text-brand-muted">
            Aparelho e implante com quem já cuidou de mais de 1.000 pacientes em Paranaguá. Parcelamos
            e explicamos tudo antes de começar.
          </p>
        </div>

        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-2">
          <div className="group relative">
            <div className="absolute -inset-0.5 rounded-[1.35rem] bg-gradient-to-r from-brand-deep to-brand-steel opacity-20 blur transition duration-500 group-hover:opacity-30" />
            <div className="relative overflow-hidden rounded-[1.25rem] border border-brand-ink/8 bg-white shadow-brand-lg transition hover:border-brand-deep/20">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1598256989809-394fa4f6cd26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxkZW50YWwlMjBicmFjZXN8ZW58MHx8fHwxNzcwMTUzMDEwfDA&ixlib=rb-4.1.0&q=85"
                  alt="Aparelhos ortodônticos"
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-brand-deep px-4 py-2 text-xs font-bold uppercase tracking-wider text-white shadow-md">
                  <Sparkles className="h-3.5 w-3.5" />
                  Destaque
                </div>
              </div>

              <div className="p-8">
                <h3 className="font-display text-2xl font-semibold text-brand-ink">Aparelhos ortodônticos</h3>
                <p className="mt-3 leading-relaxed text-brand-muted">
                  Alinhe seus dentes e sorria sem vergonha. Aparelho fixo, estético ou quase invisível —
                  a gente indica o melhor para o seu caso.
                </p>

                <div className="mt-6 space-y-3">
                  {[
                    'Aparelho quase invisível — ninguém percebe',
                    'Resultado visível em poucos meses',
                    'Parcelamento facilitado no cartão',
                    'Acompanhamento em cada consulta',
                  ].map((line) => (
                    <div key={line} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-wash ring-1 ring-brand-ink/5">
                        <Check className="h-3.5 w-3.5 text-brand-deep" strokeWidth={2.5} />
                      </div>
                      <p className="text-sm text-brand-ink">{line}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-brand-ink/6 bg-gradient-to-br from-brand-wash to-white p-4">
                  <div className="flex items-center justify-around text-center">
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-deep">500+</div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        Pacientes
                      </div>
                    </div>
                    <div className="h-12 w-px bg-brand-ink/10" />
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-deep">98%</div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        Satisfação
                      </div>
                    </div>
                    <div className="h-12 w-px bg-brand-ink/10" />
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-deep">15</div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        Anos
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleWhatsAppClick('aparelhos ortodônticos')}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-deep py-4 text-sm font-bold uppercase tracking-wide text-white shadow-md transition hover:bg-brand-ink hover:shadow-brand-lg"
                >
                  Quero meu aparelho
                  <span aria-hidden>→</span>
                </button>
              </div>
            </div>
          </div>

          <div className="group relative">
            <div className="absolute -inset-0.5 rounded-[1.35rem] bg-gradient-to-r from-brand-ink to-brand-steel opacity-15 blur transition duration-500 group-hover:opacity-25" />
            <div className="relative overflow-hidden rounded-[1.25rem] border border-brand-ink/8 bg-white shadow-brand-lg transition hover:border-brand-ink/20">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1593022356769-11f762e25ed9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBpbXBsYW50fGVufDB8fHx8MTc3MDE1MzAxNXww&ixlib=rb-4.1.0&q=85"
                  alt="Implantes dentários"
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-brand-ink px-4 py-2 text-xs font-bold uppercase tracking-wider text-white shadow-md">
                  <Star className="h-3.5 w-3.5 fill-brand-gold text-brand-gold" strokeWidth={0} />
                  Mais procurado
                </div>
              </div>

              <div className="p-8">
                <h3 className="font-display text-2xl font-semibold text-brand-ink">Implantes dentários</h3>
                <p className="mt-3 leading-relaxed text-brand-muted">
                  Volte a comer e sorrir com segurança. Implante com acompanhamento do início ao fim — a
                  gente cuida de você em cada etapa.
                </p>

                <div className="mt-6 space-y-3">
                  {[
                    'Parece um dente de verdade',
                    'Solução que dura muitos anos',
                    'Procedimento seguro e bem explicado',
                    'Cuidado depois da cirurgia',
                  ].map((line) => (
                    <div key={line} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-wash ring-1 ring-brand-ink/5">
                        <Check className="h-3.5 w-3.5 text-brand-ink" strokeWidth={2.5} />
                      </div>
                      <p className="text-sm text-brand-ink">{line}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-brand-ink/6 bg-gradient-to-br from-brand-cream to-brand-wash/60 p-4">
                  <div className="flex items-center justify-around text-center">
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-ink">300+</div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        Implantes
                      </div>
                    </div>
                    <div className="h-12 w-px bg-brand-ink/10" />
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-ink">100%</div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        Sucesso
                      </div>
                    </div>
                    <div className="h-12 w-px bg-brand-ink/10" />
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-ink">5.0</div>
                      <div className="text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        Nota
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleWhatsAppClick('implantes dentários')}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-ink py-4 text-sm font-bold uppercase tracking-wide text-white shadow-md transition hover:bg-brand-deep hover:shadow-brand-lg"
                >
                  Quero um implante
                  <span aria-hidden>→</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-16 max-w-4xl text-center">
          <div className="rounded-[1.25rem] border border-brand-ink/8 bg-white/90 p-10 shadow-brand-lg backdrop-blur-sm">
            <p className="font-display text-xl text-brand-ink md:text-2xl">
              Não sabe qual tratamento escolher?
            </p>
            <p className="mt-3 text-brand-muted">
              Chame no WhatsApp — avaliação sem compromisso e parcelamento facilitado.
            </p>
            <button
              type="button"
              onClick={() => handleWhatsAppClick('avaliação sem compromisso')}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#128C7E] px-8 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-md transition hover:bg-[#0f7a6e] hover:shadow-lg"
            >
              Avaliação sem compromisso
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecialtiesSection;
